/* eslint-disable max-lines */

/**
 * MapboxBackground Component
 *
 * Main Mapbox GL component with route-based configuration.
 * Uses singleton pattern for map instance reuse across navigation.
 *
 * Features:
 * - Route-specific opacity (home: dim, browse: bright, title: very dim)
 * - Interactive country boundaries on /browse page (the map page)
 * - Slow pan animation on homepage
 * - Country click filtering
 * - Hover tooltips with film counts
 * - Content markers with clustering on browse pages
 *
 * @see docs/plans/prototype/MAPBOX_HYBRID_IMPLEMENTATION.md
 */

import type { ApiTitle } from "@lumi/data";
import React from "react";
import { useLocation } from "react-router-dom";
import { LoadingSpinner } from "../../shared/components/loading/LoadingSpinner";
import {
  getCenterForRoute,
  getMapRouteFromPathname,
  getOpacityForRoute,
  getZoomForRoute,
  MAPBOX_CONFIG,
  MapRoute,
} from "../../utils/mapbox/config";
import { addCountryLabels } from "../../utils/mapbox/countryLabels";
import {
  addCountryBoundaries,
  hideCountryBorders,
  setupCountryClick,
  setupCountryHover,
  setupCountryTooltip,
  showCountryBorders,
  updateCountryDensity,
} from "../../utils/mapbox/countryLayer";
import {
  addMarkerLayer,
  removeMarkerLayer,
  setupMarkerClick,
  setupMarkerTooltip,
  titlesToMarkerData,
  updateMarkerLayer,
} from "../../utils/mapbox/markerLayer";
import { startSlowPan, stopSlowPan } from "../../utils/mapbox/slowPanAnimation";
import { useMapContext } from "../contexts/MapContext";
import { useMapboxInstance } from "../hooks/useMapboxInstance";

/**
 * Helper: Configure attribution control visibility and positioning
 */
function configureAttributionControl(map: mapboxgl.Map, route: MapRoute): void {
  const mapContainer = map.getContainer();
  const attributionControl = mapContainer.querySelector(".mapboxgl-ctrl-attrib") as HTMLElement;
  const bottomRightContainer = mapContainer.querySelector(".mapboxgl-ctrl-bottom-right") as HTMLElement;
  const bottomLeftContainer = mapContainer.querySelector(".mapboxgl-ctrl-bottom-left") as HTMLElement;

  if (route === MapRoute.BROWSE) {
    // On browse pages, move controls up by 500px (height of rails section)
    if (attributionControl) {
      attributionControl.style.display = "block";
    }
    if (bottomRightContainer) {
      bottomRightContainer.style.bottom = "510px";
      bottomRightContainer.style.zIndex = "1";
      bottomRightContainer.style.pointerEvents = "auto";
    }
    if (bottomLeftContainer) {
      bottomLeftContainer.style.bottom = "510px";
      bottomLeftContainer.style.zIndex = "1";
      bottomLeftContainer.style.pointerEvents = "auto";
    }
  } else {
    // On other pages, hide attribution and reset positioning
    if (attributionControl) {
      attributionControl.style.display = "none";
    }
    if (bottomRightContainer) {
      bottomRightContainer.style.bottom = "0";
    }
    if (bottomLeftContainer) {
      bottomLeftContainer.style.bottom = "0";
    }
  }
}

/**
 * Helper: Setup interactive features for browse route
 */
function setupBrowseInteractions( //NOSONAR
  map: mapboxgl.Map,
  options: {
    countryCounts?: Map<string, number>;
    selectedCountry?: string | null;
    selectedTitleId?: string | null;
    onCountryClick?: (countryName: string | null) => void;
    onTitleClick?: (titleId: string | null) => void;
    markerTitles?: ApiTitle[];
    markerFilterSummary?: string;
  },
): {
  hoverCleanup?: () => void;
  clickCleanup?: () => void;
  backgroundClickCleanup?: () => void;
  tooltipCleanup?: () => void;
  markerClickCleanup?: () => void;
  markerTooltipCleanup?: () => void;
} {
  const {
    countryCounts,
    selectedCountry,
    selectedTitleId,
    onCountryClick,
    onTitleClick,
    markerTitles,
    markerFilterSummary,
  } = options;

  const cleanups: {
    hoverCleanup?: () => void;
    clickCleanup?: () => void;
    backgroundClickCleanup?: () => void;
    tooltipCleanup?: () => void;
    markerClickCleanup?: () => void;
    markerTooltipCleanup?: () => void;
  } = {};

  // Add country boundaries on all browse pages
  if (map.getLayer(MAPBOX_CONFIG.countryBoundaries.fillLayerId)) {
    // Update density if countryCounts changed
    if (countryCounts) {
      updateCountryDensity(map, countryCounts);
    }
  } else {
    // Add new boundaries
    addCountryBoundaries(map, countryCounts ?? new Map<string, number>());
  }

  // Add country labels layer if not already added (hidden by default)
  if (!map.getLayer("country-labels")) {
    addCountryLabels(map);
  }

  // Setup interactive features
  cleanups.hoverCleanup = setupCountryHover(map);

  // Setup click handler if callback provided
  if (onCountryClick) {
    cleanups.clickCleanup = setupCountryClick(map, selectedCountry ?? null, onCountryClick);
  }

  // Setup tooltip if we have country counts
  if (countryCounts) {
    cleanups.tooltipCleanup = setupCountryTooltip(map, countryCounts);
  }

  // Setup marker layer if we have filtered titles
  if (markerTitles && markerTitles.length > 0) {
    const markers = titlesToMarkerData(markerTitles);

    // Add or update marker layer
    if (map.getSource("content-markers")) {
      updateMarkerLayer(map, markers, markerFilterSummary);
    } else {
      addMarkerLayer(map, markers, markerFilterSummary);
    }

    // Setup marker interactions
    if (onTitleClick) {
      cleanups.markerClickCleanup = setupMarkerClick(map, onTitleClick, selectedTitleId ?? null);
    }

    // Setup marker tooltips
    cleanups.markerTooltipCleanup = setupMarkerTooltip(map);
  } else {
    // Remove marker layer if no titles
    removeMarkerLayer(map);
  }

  // Setup unified background click handler
  // This checks all layers and clears the appropriate filters
  if (onCountryClick && onTitleClick) {
    const onBackgroundClick = (e: mapboxgl.MapMouseEvent) => {
      // Check what was clicked (in order of priority: markers > countries > background)
      const markerFeatures = map.queryRenderedFeatures(e.point, {
        layers: ["content-markers-clusters", "content-markers-unclustered"],
      });

      const countryFeatures = map.queryRenderedFeatures(e.point, {
        layers: [MAPBOX_CONFIG.countryBoundaries.fillLayerId],
      });

      // If clicked on markers or countries, their click handlers will fire
      // Only clear filters if clicking completely empty space
      if (markerFeatures.length === 0 && countryFeatures.length === 0) {
        // Clear both filters when clicking background
        onCountryClick(null);
        onTitleClick(null);
      }
    };

    map.on("click", onBackgroundClick);
    cleanups.backgroundClickCleanup = () => {
      map.off("click", onBackgroundClick);
    };
  }

  return cleanups;
}

/**
 * Helper: Configure map interactions based on route
 */
function configureMapInteractions(
  map: mapboxgl.Map,
  route: MapRoute,
  options: {
    countryCounts?: Map<string, number>;
    selectedCountry?: string | null;
    selectedTitleId?: string | null;
    onCountryClick?: (countryName: string | null) => void;
    onTitleClick?: (titleId: string | null) => void;
    markerTitles?: ApiTitle[];
    markerFilterSummary?: string;
  },
): {
  hoverCleanup?: () => void;
  clickCleanup?: () => void;
  backgroundClickCleanup?: () => void;
  tooltipCleanup?: () => void;
  markerClickCleanup?: () => void;
  markerTooltipCleanup?: () => void;
} {
  const disableInteractions = () => {
    map.dragPan.disable();
    map.scrollZoom.disable();
    map.boxZoom.disable();
    map.touchZoomRotate.disable();
    map.doubleClickZoom.disable();
  };

  const enableInteractions = () => {
    map.dragPan.enable();
    map.scrollZoom.enable();
    map.boxZoom.enable();
    map.touchZoomRotate.enable();
    map.doubleClickZoom.enable();
  };

  switch (route) {
    case MapRoute.HOME:
    case MapRoute.SIGNIN:
    case MapRoute.SIGNUP:
      disableInteractions();
      startSlowPan(map);
      // Remove markers on non-browse routes
      removeMarkerLayer(map);
      // Hide country boundaries on non-browse routes
      hideCountryBorders(map);
      if (map.getLayer(MAPBOX_CONFIG.countryBoundaries.fillLayerId)) {
        map.setPaintProperty(MAPBOX_CONFIG.countryBoundaries.fillLayerId, "fill-opacity", 0);
      }
      return {};

    case MapRoute.BROWSE:
      enableInteractions();
      // Restore country boundaries opacity on browse page
      if (map.getLayer(MAPBOX_CONFIG.countryBoundaries.fillLayerId)) {
        map.setPaintProperty(MAPBOX_CONFIG.countryBoundaries.fillLayerId, "fill-opacity", [
          "case",
          ["boolean", ["feature-state", "hasContent"], false],
          ["number", ["feature-state", "density"], 0],
          0,
        ]);
      }
      return setupBrowseInteractions(map, options);

    case MapRoute.TITLE:
    case MapRoute.NONE:
    default:
      disableInteractions();
      // Remove markers on non-browse routes
      removeMarkerLayer(map);
      // Hide country boundaries on non-browse routes
      hideCountryBorders(map);
      if (map.getLayer(MAPBOX_CONFIG.countryBoundaries.fillLayerId)) {
        map.setPaintProperty(MAPBOX_CONFIG.countryBoundaries.fillLayerId, "fill-opacity", 0);
      }
      return {};
  }
}

interface MapboxBackgroundProps {
  /** Map of country names to title counts (for glow visualization) */
  readonly countryCounts?: Map<string, number>;

  /** Currently selected country (for highlighting) */
  readonly selectedCountry?: string | null;

  /** Currently selected title ID (for marker highlighting/filtering) */
  readonly selectedTitleId?: string | null;

  /** Callback when user clicks a country (null to reset filter) */
  readonly onCountryClick?: (countryName: string | null) => void;

  /** Callback when user clicks an individual title marker (null to reset filter) */
  readonly onTitleClick?: (titleId: string | null) => void;

  /** Blur intensity for the map (in pixels, 0 = no blur) */
  readonly blurAmount?: number;

  /** Filtered titles for marker visualization */
  readonly markerTitles?: ApiTitle[];

  /** Human-readable filter summary for cluster tooltips */
  readonly markerFilterSummary?: string;

  /** Additional CSS classes */
  readonly className?: string;
}

/**
 * Mapbox GL background map with route-based configuration
 *
 * Usage:
 * ```tsx
 * <MapboxBackground
 *   countryCounts={countryCounts}
 *   selectedCountry={selectedCountry}
 *   onCountryClick={handleCountryClick}
 *   markerTitles={filteredTitles}
 *   className="flex-1 relative w-screen"
 * />
 * ```
 */
export function MapboxBackground({
  countryCounts,
  selectedCountry,
  selectedTitleId,
  onCountryClick,
  onTitleClick,
  blurAmount = 0,
  markerTitles,
  markerFilterSummary,
  className = "",
}: MapboxBackgroundProps) {
  const { containerRef, map, isLoaded } = useMapboxInstance();
  const location = useLocation();
  const { setMapContext } = useMapContext();

  // Track previous route to avoid resetting zoom on non-route changes (e.g., country selection)
  const previousRouteRef = React.useRef<MapRoute | null>(null);

  // Provide map instance to context for other components to use
  React.useEffect(() => {
    setMapContext({ map, isMapLoaded: isLoaded });
  }, [map, isLoaded, setMapContext]);

  // Configure map based on current route
  React.useEffect(() => {
    if (!map || !isLoaded) return;

    const route = getMapRouteFromPathname(location.pathname);
    const opacity = getOpacityForRoute(route);
    const zoom = getZoomForRoute(route);
    const center = getCenterForRoute(route);

    // Configure attribution controls
    configureAttributionControl(map, route);

    // Set satellite tile opacity based on route
    map.setPaintProperty(MAPBOX_CONFIG.customTiles.layerId, "raster-opacity", opacity);

    // Only set zoom and center when route actually changes
    if (previousRouteRef.current !== route) {
      map.setZoom(zoom);
      map.setCenter(center);
      previousRouteRef.current = route;
    }

    // Configure interactions and get cleanup functions
    const cleanups = configureMapInteractions(map, route, {
      countryCounts,
      selectedCountry,
      selectedTitleId,
      onCountryClick,
      onTitleClick,
      markerTitles,
      markerFilterSummary,
    });

    // Cleanup all interaction listeners when leaving route or unmounting
    return () => {
      stopSlowPan();
      cleanups.hoverCleanup?.();
      cleanups.clickCleanup?.();
      cleanups.backgroundClickCleanup?.();
      cleanups.tooltipCleanup?.();
      cleanups.markerClickCleanup?.();
      cleanups.markerTooltipCleanup?.();
    };
  }, [
    map,
    isLoaded,
    location.pathname,
    countryCounts,
    selectedCountry,
    selectedTitleId,
    onCountryClick,
    onTitleClick,
    markerTitles,
    markerFilterSummary,
  ]);

  // Handle cursor enter/leave for border fade animation (browse page only)
  React.useEffect(() => {
    if (!map || !isLoaded) return;

    const route = getMapRouteFromPathname(location.pathname);
    if (route !== MapRoute.BROWSE) return;

    const mapContainer = map.getContainer();

    const onMouseEnter = () => {
      showCountryBorders(map);
    };

    const onMouseLeave = () => {
      hideCountryBorders(map);
    };

    mapContainer.addEventListener("mouseenter", onMouseEnter);
    mapContainer.addEventListener("mouseleave", onMouseLeave);

    return () => {
      mapContainer.removeEventListener("mouseenter", onMouseEnter);
      mapContainer.removeEventListener("mouseleave", onMouseLeave);
    };
  }, [map, isLoaded, location.pathname]);

  return (
    <div
      className={`mapbox-background ${className}`}
      style={{
        filter: blurAmount > 0 ? `blur(${blurAmount}px)` : "none",
        transition: "filter 0.3s ease-in-out",
      }}
    >
      {/* Map container */}
      <div ref={containerRef} className="w-full h-full" />

      {/* Loading state */}
      {!isLoaded && (
        <div className="absolute inset-0 bg-black flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      )}
    </div>
  );
}
